<template>
  <div v-clickOutside="close" class="fixed bottom-20 right-20 md:bottom-0 md:right-0 z-50 flex md:relative">
    <div
      class="flex items-center text-14 cursor-pointer"
      @click="showDropdown"
    >
      <ButtonModule
        class="bg-link-color md:bg-transparent p-17 rounded-full shadow-filter-button opacity-80 md:opacity-100
               transform ease-in-out transition-transform md:p-11 md:rounded-6 md:shadow-none md:border-none"
        :class="classes"
      >
        <div class="flex items-center">
          <svg class="h-18 w-18 md:w-14 md:h-14 md:mr-10 fill-current text-white md:text-app-color " width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.78534 0.109129C5.59597 0.109129 4.62286 1.08225 4.62286 2.27161L4.62286 13.084L8.94782 13.084L8.94782 2.27161C8.94782 1.08225 7.9747 0.109129 6.78534 0.109129ZM32.7351 0.109131C31.5457 0.109131 30.5726 1.08225 30.5726 2.27161L30.5726 21.7339L34.8976 21.7339L34.8976 2.27161C34.8976 1.08225 33.9245 0.109131 32.7351 0.109131ZM2.46038 21.7339L4.62286 21.7339L4.62286 36.8713C4.62286 38.0606 5.59597 39.0338 6.78534 39.0338C7.9747 39.0338 8.94782 38.0606 8.94782 36.8713L8.94782 21.7339L11.1103 21.7339C12.2997 21.7339 13.2728 20.7608 13.2728 19.5714C13.2728 18.3821 12.2997 17.409 11.1103 17.409L2.46038 17.409C1.27101 17.409 0.2979 18.3821 0.2979 19.5714C0.2979 20.7608 1.27101 21.7339 2.46038 21.7339ZM24.0852 8.75905L21.9227 8.75905L21.9227 2.27161C21.9227 1.08225 20.9496 0.10913 19.7602 0.10913C18.5709 0.10913 17.5977 1.08225 17.5977 2.27161L17.5977 8.75905L15.4353 8.75905C14.2459 8.75905 13.2728 9.73216 13.2728 10.9215C13.2728 12.1109 14.2459 13.084 15.4353 13.084L24.0852 13.084C25.2745 13.084 26.2477 12.1109 26.2477 10.9215C26.2477 9.73216 25.2745 8.75905 24.0852 8.75905ZM19.7602 39.0338C20.9496 39.0338 21.9227 38.0606 21.9227 36.8713L21.9227 17.409L17.5977 17.409L17.5977 36.8713C17.5977 38.0606 18.5709 39.0338 19.7602 39.0338ZM26.2477 28.2214C26.2477 29.4107 27.2208 30.3838 28.4101 30.3838L30.5726 30.3838L30.5726 36.8713C30.5726 38.0606 31.5457 39.0338 32.7351 39.0338C33.9245 39.0338 34.8976 38.0606 34.8976 36.8713L34.8976 30.3838L37.0601 30.3838C38.2494 30.3838 39.2225 29.4107 39.2225 28.2214C39.2225 27.032 38.2494 26.0589 37.0601 26.0589L28.4101 26.0589C27.2208 26.0589 26.2477 27.032 26.2477 28.2214Z" />
          </svg>
          <p class="hidden md:block text-16 font-semibold">Filter</p>
        </div>
      </ButtonModule>
    </div>

    <InfoDropdown
      v-if="show"
      title="Filter tags"
      :classes="isMobile ? 'w-232 fixed bottom-65 right-0 rounded-10' : 'right-0 top-40 w-232 rounded-10'"
      title-classes="text-16 font-bold text-app-color px-12 mb-6"
    >
      <div v-for="(key, value) in tags" :key="value">
        <p v-if="key.length > 0" class="px-12 truncate font-medium text-12 mb-5">{{ bookTitle(value) }}</p>
        <div v-if="key.length > 0" class="pb-8 flex flex-wrap justify-between px-12 overflow-y-auto">
          <div v-for="tag in key" :key="tag.id" class="mb-10 flex items-center w-67 px-1 cursor-pointer">
            <div
              class="flex items-center rounded-8 w-full h-23 px-5 border-3 border-transparent "
              :style="`background-color: #${tag.color.toString(16)}`"
              :class="{ 'tag-border': isSelected(tag) }"
              @click="toggleTag(tag, isSelected(tag))"
            >
              <div v-if="!!tag.name" class="w-full h-full text-11 font-medium flex items-center justify-center text-dark-600">
                <p class="text-12">#</p>
                <p class="truncate lowercase">{{ tag.name }}</p>
              </div>
            </div>
          </div>
          <div class="w-70" />
        </div>
      </div>
    </InfoDropdown>
  </div>
</template>

<script>
import InfoDropdown from '@/components/shared/InfoDropdown'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'

import { defineComponent, computed, ref } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  components: { InfoDropdown, ButtonModule },

  props: {
    tags: Object
  },

  emits: ['showDropdown', 'toggleTag'],

  setup (props, { emit }) {
    const { state, getters } = useStore()

    const show = ref(false)

    const books = computed(() => state.book.books)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const selectedTags = ref([])

    const isSelected = computed(() => tag => selectedTags.value.some(item => item.id === tag.id))

    const classes = computed(() => {
      const prefix = show.value ? '' : 'hover:'
      return `${isDarkMode.value ? `md:${prefix}bg-dark-500` : `md:${prefix}bg-menu-hover`} rounded-6 anim-color-200`
    })

    function showDropdown () {
      show.value = !show.value
    }

    const bookTitle = computed(() => (value) => {
      const book = books.value.find(b => +b.id === +value)
      return book?.title
    })

    function close () {
      show.value = false
    }

    function toggleTag (tag, selected) {
      if (selected) {
        selectedTags.value = selectedTags.value.filter(item => item.id !== tag.id)
      } else {
        selectedTags.value.push(tag)
      }

      const tagIds = selectedTags.value.map(item => item.id)

      emit('toggleTag', tagIds)
    }

    return {
      // data
      show,
      // computeds
      classes,
      isMobile,
      bookTitle,
      isSelected,
      isDarkMode,
      // functions
      close,
      showDropdown,
      toggleTag
    }
  }
})
</script>
